import React from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import SelectField, { SelectOption } from "../../components/ui/SelectField";
import ChartLoading from "../../insight/components/ChartLoading";

export interface ImportListSectionProps {
  /** When false, component renders null (e.g. when wizard is open or app not connected) */
  visible: boolean;
  loading: boolean;
  filteredImports: any[];
  /** Total imports count before filter (for empty vs no-results message) */
  importsCount: number;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  statusFilter: "" | "done" | "draft" | "failed" | "processing";
  onStatusFilterChange: (value: "" | "done" | "draft" | "failed" | "processing") => void;
  onNewImportClick: () => void;
  renderCard: (item: any) => React.ReactNode;
}

/**
 * Dumb UI for Imports history: title, search, status filter, New Import button, and list of cards.
 * State and API stay in parent; this component only receives props and calls callbacks.
 */
const ImportListSection: React.FC<ImportListSectionProps> = ({
  visible,
  loading,
  filteredImports,
  importsCount,
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  onNewImportClick,
  renderCard,
}) => {
  const { t } = useTranslation();

  if (!visible) return null;

  const statusFilterOptions: SelectOption[] = [
    { value: "", label: t("Status") },
    { value: "done", label: t("Done") },
    { value: "draft", label: t("Draft") },
    { value: "failed", label: t("Failed") },
    { value: "processing", label: t("Processing") },
  ];

  return (
    <div className="max-w-[820px] px-2">
      <div>
        <h1 className="text-[18px] font-bold leading-[22px] text-[#1f2937] pt-[14px]">
          {t("Imports history")}
        </h1>
        <p className="mt-1 text-[14px] font-normal leading-5 text-[#6b7280]">
          {t("View and manage the history of imported files")}
        </p>
      </div>
      <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mt-8">
        <div className="flex h-[38px] w-full min-w-0 items-center gap-3">
          <div className="relative h-full min-w-0 flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-[14px] w-[14px] -translate-y-1/2 text-[#6b7280]"
              aria-hidden
            />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={t("Find import")}
              className="h-full w-full rounded-[8px] border border-[#e5e7eb] bg-white pl-9 pr-3 text-[15px] font-medium leading-[18px] text-[#1f2937] placeholder:text-[#6b7280] focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              aria-label={t("Find import")}
            />
          </div>
          <div className="h-[38px] w-[150px] shrink-0">
            <SelectField
              options={statusFilterOptions}
              placeholder={t("Status")}
              value={
                (statusFilter
                  ? {
                      value: statusFilter,
                      label: t(
                        statusFilter === "done"
                          ? "Done"
                          : statusFilter === "draft"
                            ? "Draft"
                            : statusFilter === "processing"
                              ? "Processing"
                              : "Failed"
                      ),
                    }
                  : null) as SelectOption | null
              }
              onChange={(selected: any) =>
                onStatusFilterChange(
                  (selected?.value ?? "") as "" | "done" | "draft" | "failed" | "processing"
                )
              }
              isClearable
              aria-label={t("Status")}
            />
          </div>
          <button
            type="button"
            onClick={onNewImportClick}
            className="h-[38px] w-[135px] shrink-0 rounded-[8px] bg-[#2563eb] text-[15px] font-semibold leading-[18px] text-white shadow-xs hover:bg-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
          >
            {t("New Import")}
          </button>
        </div>
      </div>
      {loading ? (
        <div className="mt-6 flex min-h-[120px] items-center justify-center gap-2 text-[14px] text-[#6b7280]">
          <ChartLoading height={120} label={t("Loading...")} />
        </div>
      ) : filteredImports.length > 0 ? (
        <div className="mt-6 space-y-2">
          {filteredImports.map((item: any) => (
            <React.Fragment key={item.importId}>{renderCard(item)}</React.Fragment>
          ))}
        </div>
      ) : importsCount > 0 ? (
        <p className="mt-6 text-sm text-[#6b7280]">{t("No imports match your search")}</p>
      ) : null}
    </div>
  );
};

export default ImportListSection;
